import React, { useEffect, useState } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import Avatar from '../../components/Avatar';
import Tenant from '../../components/Tenant';
import {
  getServiceProviders,
  getServiceProvidersFilteredByPropertyId,
} from 'src/services/client';
import {
  IServiceProvider,
  IServiceProviderResponse,
} from 'src/services/api/ServiceRequestDashboard/apiContracts';
import Seperator from 'src/components/Seperator/seperator';
import { Images } from 'src/Tenants';
import {
  adaptToCategoryPills,
  IContactPickerState,
  isPopulatedContact,
} from 'src/pages/ServiceRequest/helpers';
import ServiceProviderList from 'src/pages/ServiceRequest/ServiceProviderList';
import './ContactPicker.scss';

interface ContactPickerProps {
  serviceRequest: any;
  setFormData: any;
  from: any;
  isLegacyTable?: boolean;
}

export const ContactPicker = ({
  serviceRequest,
  setFormData,
  from,
  isLegacyTable,
}: ContactPickerProps) => {
  // State using a single state object like in the old class component.
  const [state, setState] = useState<IContactPickerState>({
    dropdownOpen: true,
    openCalendar: false,
    contact: {},
    scheduling_url: '',
    addEvent: true,
  });
  const [propertySpecificContactsFromAPI, setPropertySpecificContactsFromAPI] =
    useState<IServiceProvider[]>(null);
  const [unfilteredContactsFromAPI, setUnfilteredContactsFromAPI] =
    useState<any>(null);
  const [isLoadingPropertyProviders, setIsLoadingPropertyProviders] =
    useState<boolean>(true);
  const [isLoadingUnfilteredProviders, setIsLoadingUnfilteredProviders] =
    useState(true);
  const isLoading: boolean =
    isLoadingPropertyProviders || isLoadingUnfilteredProviders;

  const fetchProviders = async () => {
    try {
      setIsLoadingUnfilteredProviders(true);
      const params = {
        Name: serviceRequest.serviceType
          ? serviceRequest.serviceType
          : serviceRequest.issueType.name,
        id: serviceRequest.propertyId,
        Type: from === 'dashboard' ? 'Bulk' : null,
      };

      // Todo: I think we should define a proper interface for this response in TS.
      const responseData: any = await getServiceProviders(params);
      setUnfilteredContactsFromAPI(responseData);
    } catch (error) {
      console.error(
        'Unexpected error when fetching the service providers:',
        error
      );
    } finally {
      setIsLoadingUnfilteredProviders(false);
    }
  };

  const fetchProvidersFilteredById = async () => {
    try {
      setIsLoadingPropertyProviders(true);
      const response: IServiceProviderResponse =
        await getServiceProvidersFilteredByPropertyId(
          serviceRequest.propertyId
        );
      setPropertySpecificContactsFromAPI(response.data);
    } catch (error) {
      console.error(
        `Error fetching service providers for property ID ${serviceRequest.propertyId}: ${error.message}`,
        error
      );
    } finally {
      setIsLoadingPropertyProviders(false);
    }
  };

  useEffect(() => {
    fetchProvidersFilteredById();
    fetchProviders();
  }, []);

  const handleContactSelect = (selectedContact: IServiceProvider) => {
    setState((prev: IContactPickerState) => ({
      ...prev,
      contact: selectedContact,
      dropdownOpen: false,
    }));
    setFormData({ userId: selectedContact.userId });
  };

  const handleFullContactSelect = (selectedContact: any) => {
    setState((prev: IContactPickerState) => ({
      ...prev,
      contact: selectedContact,
      dropdownOpen: false,
    }));
    setFormData({ userId: selectedContact.id });
  };

  return (
    <div className="contactPicker">
      <OutsideClickHandler
        onOutsideClick={() =>
          setState((prev: IContactPickerState) => ({
            ...prev,
            dropdownOpen: false,
          }))
        }
      >
        {/* Selected contact display. */}
        <div
          className="contactPicker__selectedDisplay"
          onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            e.preventDefault();
            setState((prev: IContactPickerState) => ({
              ...prev,
              dropdownOpen: !state.dropdownOpen,
            }));
          }}
        >
          {isPopulatedContact(state.contact) ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Tenant
                headline={`${state.contact.firstname} ${state.contact.lastname}`}
                strapline={
                  from === 'dashboard'
                    ? `${state.contact.role} ${state.contact.servicesProvided !== null ? ' - ' + state.contact.servicesProvided : ''}`
                    : state.contact.role || state.contact.type || null
                }
                strapLineItemsListRole={`${state.contact.role}`}
                strapLineItemsList={adaptToCategoryPills(
                  state.contact.servicesProvided
                )}
                avatar={
                  <Avatar
                    badge={
                      state.contact.role === 'Service Provider' ||
                      state.contact.role === 'Service Manager'
                        ? 'service-provider'
                        : 'property-manager'
                    }
                    url={state.contact.profileImage}
                    size={undefined}
                  />
                }
                company={state.contact.companyName}
              />
            </div>
          ) : (
            <div className="contact-picker-selection-font">
              Please make a selection below.
            </div>
          )}
        </div>
        {/* Entire list of contacts, no longer a dropdown. */}
        {isLoading ? (
          <div className="contactPicker__dropdown">
            <div
              style={{
                position: 'relative',
                height: '300px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                paddingTop: '40px', // Adjust this value to position the loader lower.
              }}
            >
              <img
                style={{ width: '100px', height: '100px' }}
                src={Images?.loader}
              />
            </div>
          </div>
        ) : (
          <div className="contactPicker__dropdown">
            {/* Main content container - use flexbox for responsive layout. */}
            <div>
              {/* Property-specific providers section - always render this. */}
              {isLegacyTable && (
                <>
                  <div>
                    <div className="contact-picker-headline">
                      Service Providers Associated with this Property
                    </div>
                    {propertySpecificContactsFromAPI &&
                    propertySpecificContactsFromAPI.length > 0 ? (
                      <ServiceProviderList
                        providers={propertySpecificContactsFromAPI}
                        handleContactSelect={handleContactSelect}
                        from={from}
                      />
                    ) : (
                      <div
                        style={{
                          padding: '8px',
                          color: '#666',
                          fontStyle: 'italic',
                        }}
                      >
                        No service providers are associated with this property.
                      </div>
                    )}
                  </div>
                  {/* Show separator between sections on mobile. */}
                  <div>
                    <Seperator />
                  </div>
                </>
              )}
              {/* Unfiltered providers section - only render if there's data. */}
              {unfilteredContactsFromAPI &&
                unfilteredContactsFromAPI.length > 0 && (
                  <div>
                    {isLegacyTable && (
                      <div className="contact-picker-headline">
                        Service Providers not associated with this Property
                      </div>
                    )}
                    <ServiceProviderList
                      providers={unfilteredContactsFromAPI}
                      handleContactSelect={handleFullContactSelect}
                      from={from}
                    />
                  </div>
                )}
            </div>
          </div>
        )}
      </OutsideClickHandler>
    </div>
  );
};

export default ContactPicker;
